/**
 * Shopify Instagram-Style Video Reels Section Controller
 * Optimized for 60fps performance & lightweight loading
 */
if (!customElements.get('m-reels-section')) {
  customElements.define('m-reels-section', class MReelsSection extends HTMLElement {
    constructor() {
      super();
      this.modal = null;
      this.feed = null;
      this.cards = [];
      this.items = [];
      this.activeVideo = null;
      this.activeIndex = 0;
      this.isMuted = true; // start muted for autoplay compliance
      this.lastTapTime = 0;
      this.touchStartY = 0;
      this.autoTimer = null;
      this.isInteracting = false;
      this.isSectionVisible = false;

      this.init();
    }

    init() {
      this.cards = Array.from(this.querySelectorAll('.m-reel-card'));
      this.modal = this.querySelector('.m-reels-modal');
      this.track = this.querySelector('.js-reels-track') || this.querySelector('.m-reels__slider') || this.querySelector('.m-reels__grid');

      this.setupSectionObserver();
      this.setupCardAutoplay();
      this.setupAutoSlide();

      if (!this.modal) return;

      this.feed = this.modal.querySelector('.m-reels-modal__feed');
      this.items = Array.from(this.modal.querySelectorAll('.m-reels-modal__item'));
      this.counter = this.modal.querySelector('.js-reel-counter');
      this.progressFill = this.modal.querySelector('.m-reels-modal__progress-fill');
      this.muteBtns = Array.from(this.modal.querySelectorAll('.js-mute-toggle'));

      this.bindEvents();
      this.setupObserver();
    }

    setupSectionObserver() {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isSectionVisible = entry.isIntersecting;
        });
      }, { threshold: 0.1 });
      sectionObserver.observe(this);
    }

    setupAutoSlide() {
      const isAutoplay = this.dataset.autoplay === 'true';
      if (!isAutoplay || !this.track || this.cards.length <= 1) return;

      const speed = parseInt(this.dataset.speed || '3000', 10);

      const startTimer = () => {
        this.stopAutoSlide();
        this.autoTimer = setInterval(() => {
          if (!this.isSectionVisible || this.isInteracting || (this.modal && this.modal.classList.contains('is-active'))) {
            return;
          }
          this.nextSlide();
        }, speed);
      };

      startTimer();

      // Pause auto-slide on touch/mouse interaction and resume after pause
      const pause = () => {
        this.isInteracting = true;
      };
      const resume = () => {
        clearTimeout(this.resumeTimeout);
        this.resumeTimeout = setTimeout(() => {
          this.isInteracting = false;
        }, 3000);
      };

      this.track.addEventListener('mouseenter', pause);
      this.track.addEventListener('mouseleave', () => {
        this.isInteracting = false;
      });
      this.track.addEventListener('touchstart', pause, { passive: true });
      this.track.addEventListener('touchend', resume, { passive: true });
      this.track.addEventListener('scroll', () => {
        pause();
        resume();
      }, { passive: true });
    }

    stopAutoSlide() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    nextSlide() {
      if (!this.track || this.cards.length === 0) return;

      const firstCard = this.cards[0];
      const secondCard = this.cards[1];
      const gap = secondCard ? (secondCard.offsetLeft - firstCard.offsetLeft - firstCard.offsetWidth) : 12;
      const step = firstCard.offsetWidth + Math.max(0, gap);

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const currentScroll = this.track.scrollLeft;

      if (currentScroll >= maxScroll - 15) {
        // Infinite loop: Smooth scroll back to start
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.track.scrollTo({ left: currentScroll + step, behavior: 'smooth' });
      }
    }

    setupCardAutoplay() {
      // Lazy load & autoplay card videos ONLY when in/near viewport (Low Load + Autoplay)
      const cardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('.m-reel-card__video');
          if (!video) return;

          if (entry.isIntersecting) {
            const videoUrl = video.dataset.src || card.dataset.videoUrl;
            if (videoUrl && !video.src) {
              video.src = videoUrl;
              video.load();
            }
            video.muted = true;
            video.play().catch(() => {});
          } else {
            // Pause off-screen videos to keep load ultra-low
            video.pause();
          }
        });
      }, { rootMargin: '100px 0px', threshold: 0.1 });

      this.cards.forEach((card) => cardObserver.observe(card));
    }

    bindEvents() {
      // Open modal on card click
      this.cards.forEach((card, index) => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('.m-reel-card__shop-btn')) {
            return;
          }
          this.openModal(index);
        });
      });

      // Close modal events
      const closeBtn = this.modal.querySelector('.js-close-modal');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeModal());
      }

      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });

      // Desktop Wheel Navigation: Throttled smooth reel snapping anywhere on screen
      let isWheeling = false;
      this.modal.addEventListener('wheel', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isWheeling) return;

        if (Math.abs(e.deltaY) > 10) {
          isWheeling = true;
          if (e.deltaY > 0) {
            this.navigateReel(1);
          } else {
            this.navigateReel(-1);
          }
          setTimeout(() => {
            isWheeling = false;
          }, 450);
        }
      }, { passive: false });

      // Prevent background touch scrolling when swiping outside feed
      this.modal.addEventListener('touchmove', (e) => {
        if (!e.target.closest('.m-reels-modal__feed')) {
          e.preventDefault();
        }
      }, { passive: false });

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!this.modal.classList.contains('is-active')) return;
        if (e.key === 'Escape') {
          this.closeModal();
        } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
          e.preventDefault();
          this.navigateReel(1);
        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
          e.preventDefault();
          this.navigateReel(-1);
        } else if (e.key === ' ') {
          e.preventDefault();
          this.togglePlayPause();
        } else if (e.key === 'm' || e.key === 'M') {
          this.toggleMute();
        }
      });

      // Mute / Unmute toggles
      this.muteBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleMute();
        });
      });

      // Item level events (Play/Pause, Like, Share, Up/Down)
      this.items.forEach((item, index) => {
        const video = item.querySelector('.m-reels-modal__video');
        
        // Prev / Next button clicks
        const prevBtn = item.querySelector('.js-prev-reel');
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateReel(-1);
          });
        }

        const nextBtn = item.querySelector('.js-next-reel');
        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateReel(1);
          });
        }
        
        // Tap / Click video for Play/Pause
        item.addEventListener('click', (e) => {
          if (e.target.closest('.m-reels-modal__side-bar') || e.target.closest('.m-reels-modal__bottom-bar') || e.target.closest('.m-reels-modal__top-bar')) {
            return;
          }
          this.togglePlayPause(video, item);
        });

        // Share button click
        const shareBtn = item.querySelector('.js-share-reel');
        if (shareBtn) {
          shareBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.shareReel(item);
          });
        }

        // Video progress updates
        if (video) {
          video.addEventListener('timeupdate', () => {
            if (index === this.activeIndex && video.duration) {
              const progress = (video.currentTime / video.duration) * 100;
              if (this.progressFill) {
                this.progressFill.style.width = `${progress}%`;
              }
            }
          });

          video.addEventListener('ended', () => {
            // Loop current video
            video.currentTime = 0;
            video.play().catch(() => {});
          });
        }
      });
    }

    setupObserver() {
      // Intersection Observer to detect which reel item is currently snapped in view
      const options = {
        root: this.feed,
        threshold: 0.6
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index'), 10);
            this.onReelActive(index);
          }
        });
      }, options);

      this.items.forEach((item) => observer.observe(item));
    }

    openModal(index = 0) {
      this.scrollTopPosition = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.modal.classList.add('is-active');
      document.documentElement.classList.add('m-reels-open');
      document.body.classList.add('m-reels-open');
      document.body.style.top = `-${this.scrollTopPosition}px`;
      setTimeout(() => {
        this.scrollToReel(index, false);
      }, 50);
    }

    closeModal() {
      this.modal.classList.remove('is-active');
      document.documentElement.classList.remove('m-reels-open');
      document.body.classList.remove('m-reels-open');
      document.body.style.top = '';
      if (typeof this.scrollTopPosition === 'number') {
        window.scrollTo(0, this.scrollTopPosition);
      }
      
      // Pause all videos
      this.items.forEach((item) => {
        const video = item.querySelector('.m-reels-modal__video');
        if (video) {
          video.pause();
        }
      });
      this.activeVideo = null;
    }

    scrollToReel(index, smooth = false) {
      if (index < 0 || index >= this.items.length) return;
      const targetItem = this.items[index];
      if (targetItem) {
        targetItem.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'start' });
      }
    }

    navigateReel(direction) {
      const newIndex = this.activeIndex + direction;
      if (newIndex >= 0 && newIndex < this.items.length) {
        this.scrollToReel(newIndex, true);
      }
    }

    onReelActive(index) {
      this.activeIndex = index;

      // Update counter badge
      if (this.counter) {
        this.counter.textContent = `${index + 1} / ${this.items.length}`;
      }

      // Handle video playback with lazy loading
      this.items.forEach((item, idx) => {
        const video = item.querySelector('.m-reels-modal__video');
        if (!video) return;

        if (idx === index) {
          if (video.dataset.src && !video.src) {
            video.src = video.dataset.src;
            video.load();
          }
          this.activeVideo = video;
          video.muted = this.isMuted;
          video.play().catch((err) => {
            console.log('Autoplay prevented:', err);
          });
        } else if (idx === index + 1) {
          // Preload next video in background for seamless swipe transition
          if (video.dataset.src && !video.src) {
            video.src = video.dataset.src;
            video.load();
          }
          video.pause();
        } else {
          video.pause();
        }
      });

      this.updateMuteStateIcons();
    }

    togglePlayPause(videoTarget, itemTarget) {
      const video = videoTarget || this.activeVideo;
      const item = itemTarget || this.items[this.activeIndex];
      if (!video) return;

      const indicator = item ? item.querySelector('.m-reels-modal__play-pause-indicator') : null;

      if (video.paused) {
        video.play();
        if (indicator) {
          indicator.innerHTML = `<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>`;
          this.animateIndicator(indicator);
        }
      } else {
        video.pause();
        if (indicator) {
          indicator.innerHTML = `<svg viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`;
          this.animateIndicator(indicator);
        }
      }
    }

    animateIndicator(indicator) {
      indicator.classList.remove('animate-pop');
      void indicator.offsetWidth; // Force reflow
      indicator.classList.add('animate-pop');
      setTimeout(() => {
        indicator.classList.remove('animate-pop');
      }, 700);
    }

    toggleMute() {
      this.isMuted = !this.isMuted;
      this.items.forEach((item) => {
        const video = item.querySelector('.m-reels-modal__video');
        if (video) {
          video.muted = this.isMuted;
        }
      });
      this.updateMuteStateIcons();
    }

    updateMuteStateIcons() {
      const mutedSvg = `<svg viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>`;
      const unmutedSvg = `<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;

      this.muteBtns.forEach((btn) => {
        btn.innerHTML = this.isMuted ? mutedSvg : unmutedSvg;
      });
    }

    shareReel(item) {
      const shareBtn = item.querySelector('.js-share-reel');
      const productCard = item.querySelector('.m-reels-modal__product-card');
      
      let productUrl = shareBtn ? shareBtn.getAttribute('data-product-url') : null;
      if (!productUrl && productCard) {
        productUrl = productCard.getAttribute('href');
      }
      if (!productUrl) {
        productUrl = window.location.href;
      }
      
      if (productUrl.startsWith('/')) {
        productUrl = window.location.origin + productUrl;
      }

      let productTitle = shareBtn ? shareBtn.getAttribute('data-product-title') : null;
      if (!productTitle && productCard) {
        const titleEl = productCard.querySelector('.m-reels-modal__product-title');
        if (titleEl) productTitle = titleEl.textContent.trim();
      }
      if (!productTitle) productTitle = document.title;

      if (navigator.share) {
        navigator.share({
          title: productTitle,
          url: productUrl
        }).catch(() => {});
      } else {
        navigator.clipboard.writeText(productUrl).then(() => {
          this.showToast('Product link copied to clipboard!');
        }).catch(() => {
          this.showToast('Product link copied!');
        });
      }
    }

    showToast(message) {
      let toast = document.querySelector('.m-reels-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'm-reels-toast';
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      toast.classList.add('is-show');
      setTimeout(() => toast.classList.remove('is-show'), 2500);
    }
  });
}
